import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const BG = "https://images.unsplash.com/photo-1649751361457-01d3a696c7e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxwaHlzaWNhbCUyMHRoZXJhcHklMjBtYXNzYWdlJTIwcmVoYWJpbGl0YXRpb24lMjBzZXNzaW9ufGVufDB8fHx8MTc4ODIxNTc2N3ww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tsCfg, setTsCfg] = useState({ enabled: false, site_key: "" });
  const [tsToken, setTsToken] = useState("");
  const widgetRef = useRef(null);
  const widgetId = useRef(null);

  useEffect(() => {
    api.get("/auth/turnstile-config").then(({ data }) => setTsCfg(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!tsCfg.enabled || !tsCfg.site_key) return;
    const render = () => {
      if (!window.turnstile || !widgetRef.current || widgetId.current !== null) return;
      widgetId.current = window.turnstile.render(widgetRef.current, {
        sitekey: tsCfg.site_key,
        action: "login",
        theme: "light",
        callback: (t) => { setTsToken(t); setError(""); },
        "expired-callback": () => setTsToken(""),
        "error-callback": () => setTsToken(""),
      });
    };
    if (window.turnstile) { render(); return; }
    if (!document.getElementById("cf-turnstile-script")) {
      const s = document.createElement("script");
      s.id = "cf-turnstile-script";
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      s.async = true;
      s.onload = render;
      document.body.appendChild(s);
    } else {
      const t = window.setInterval(() => {
        if (window.turnstile) { window.clearInterval(t); render(); }
      }, 150);
      return () => window.clearInterval(t);
    }
    // eslint-disable-next-line consistent-return
  }, [tsCfg]);

  useEffect(() => () => {
    if (widgetId.current !== null && window.turnstile) {
      try { window.turnstile.remove(widgetId.current); } catch { /* noop */ }
      widgetId.current = null;
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (tsCfg.enabled && tsCfg.site_key && !tsToken) {
      setError("Completa la verifica di sicurezza");
      return;
    }
    setLoading(true);
    try {
      await login(email, password, { turnstile_token: tsToken || undefined });
      navigate("/");
    } catch (err) {
      setError(apiError(err, "Accesso non riuscito"));
      setTsToken("");
      if (widgetId.current !== null && window.turnstile) window.turnstile.reset(widgetId.current);
    } finally {
      setLoading(false);
    }
  };

  const turnstileReady = !tsCfg.enabled || !tsCfg.site_key || !!tsToken;

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex w-1/2 relative items-end p-12 bg-cover bg-center" style={{ backgroundImage: `url(${BG})` }}>
        <div className="absolute inset-0 bg-gradient-to-t from-blue-900/70 via-blue-600/20 to-transparent" />
        <div className="relative text-white">
          <div className="mb-4 bg-white rounded-2xl px-6 py-4 inline-flex shadow-xl">
            <img src="/logo.png" alt="LanuseiLAB" className="h-16 w-auto" data-testid="login-logo" />
          </div>
          <p className="text-blue-100 text-lg max-w-md">Il gestionale del tuo centro di fisioterapia e riabilitazione. Semplice, veloce, sempre con te.</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex justify-center">
            <img src="/logo.png" alt="LanuseiLAB" className="h-14 w-auto" data-testid="login-logo-mobile" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Bentornato</h2>
          <p className="text-slate-500 text-sm mt-1 mb-6">Accedi al gestionale del centro</p>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label>Email</Label>
              <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl mt-1" placeholder="nome@email.it" autoComplete="email" />
            </div>
            <div>
              <Label>Password</Label>
              <div className="relative mt-1">
                <Input data-testid="login-password" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl pr-10" placeholder="••••••••" autoComplete="current-password" />
                <button type="button" data-testid="login-toggle-password" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {tsCfg.enabled && tsCfg.site_key && (
              <div className="flex flex-col items-center gap-1.5">
                <div ref={widgetRef} data-testid="login-turnstile-widget" className="min-h-[65px]" />
                <p className="text-[10px] text-slate-400 flex items-center gap-1"><ShieldCheck size={11} /> Protetto da Cloudflare Turnstile</p>
              </div>
            )}
            {tsCfg.enabled && !tsCfg.site_key && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="login-turnstile-misconfigured">
                Verifica di sicurezza non disponibile. Contatta l'amministratore.
              </p>
            )}
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2" data-testid="login-error">{error}</p>}
            <Button data-testid="login-submit" type="submit" disabled={loading || !turnstileReady} className="w-full rounded-full h-11 text-base">
              {loading ? "Accesso in corso..." : "Accedi"}
            </Button>
          </form>
          <p className="text-[10px] text-slate-400 text-center mt-10 leading-relaxed">
            Software realizzato da Multiprojects.it — Maurizio Deplano<br />AI Powered Marketing Expert, Website Development Expert
          </p>
        </div>
      </div>
    </div>
  );
}
